import { useState, useMemo, useEffect } from 'react'
import { Player } from '../types'
import { useAuthStore, usePlayerStore } from '../stores'
import { useAchievementStore } from '../stores/useAchievementStore'
import { useProStatus } from '../utils/useProStatus'
import { Achievement } from '../types'
import {
  Award, Plus, X, Trash2, Loader2, AlertCircle, CheckCircle2,
  Trophy, Medal, Shield, FileText, Crown, Target
} from 'lucide-react'

const ACHIEVEMENT_CATEGORIES = [
  { value: 'league_title', label: 'League Title', icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  { value: 'golden_boot', label: 'Golden Boot', icon: Target, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  { value: 'mvp', label: 'MVP', icon: Crown, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  { value: 'best_defender', label: 'Best Defender', icon: Shield, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { value: 'tournament_winner', label: 'Tournament Winner', icon: Medal, color: 'text-green-500', bg: 'bg-green-500/10' },
  { value: 'certificate', label: 'Certificate', icon: FileText, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  { value: 'award', label: 'Award', icon: Award, color: 'text-pink-500', bg: 'bg-pink-500/10' },
]

interface PlayerAchievementsProps {
  player?: Player
}

function PlayerAchievements({ player: playerProp }: PlayerAchievementsProps) {
  const { user } = useAuthStore()
  const { players } = usePlayerStore()
  const { achievements, loading, error, fetchAchievements, addAchievement, deleteAchievement } = useAchievementStore()
  const { isPro } = useProStatus()
  const atLimit = !isPro && achievements.length >= 3
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    title: '',
    category: '' as Achievement['category'] | '',
    description: '',
    year: '',
    competition: '',
    club: '',
  })

  const currentPlayer = useMemo(() => {
    if (playerProp) return playerProp
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [playerProp, user, players])

  const isOwner = useMemo(() => {
    if (!user || !currentPlayer) return false
    return (
      currentPlayer.scout?.toLowerCase() === user.email?.toLowerCase() ||
      currentPlayer.email?.toLowerCase() === user.email?.toLowerCase() ||
      currentPlayer.id === user.id
    )
  }, [user, currentPlayer])

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchAchievements(currentPlayer.id)
    }
  }, [currentPlayer?.id, fetchAchievements])

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      description: '',
      year: '',
      competition: '',
      club: '',
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPlayer?.id || !formData.title || !formData.category) return

    setSaving(true)
    try {
      await addAchievement({
        player_id: currentPlayer.id,
        title: formData.title,
        category: formData.category as Achievement['category'],
        description: formData.description || undefined,
        year: formData.year ? parseInt(formData.year) : undefined,
        competition: formData.competition || undefined,
        club: formData.club || undefined,
      })
      resetForm()
      setShowForm(false)
      setSuccess('Achievement added!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Failed to add achievement:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteAchievement(id)
      setDeleteConfirm(null)
      setSuccess('Achievement deleted!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Failed to delete achievement:', err)
    }
  }

  const groupedAchievements = useMemo(() => {
    const groups: Record<string, Achievement[]> = {}
    achievements.forEach(a => {
      if (!groups[a.category]) groups[a.category] = []
      groups[a.category].push(a)
    })
    return groups
  }, [achievements])

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Achievements</h2>
          <p className="text-sm text-gray-400 mt-1">{isOwner ? 'Showcase your awards, honors, and accomplishments' : 'Awards, honors, and accomplishments'}</p>
        </div>
        {isOwner && (
          atLimit ? (
            <a href="/pricing" className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-yellow-400 hover:bg-yellow-500/20 transition text-xs font-bold uppercase tracking-widest">
              <Plus size={14} />
              Upgrade for More
            </a>
          ) : (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 hover:bg-green-500/20 transition text-xs font-bold uppercase tracking-widest"
            >
              {showForm ? <X size={14} /> : <Plus size={14} />}
              {showForm ? 'Cancel' : 'Add Achievement'}
            </button>
          )
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-700/30 rounded-xl text-green-400 text-xs font-bold">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-700/30 rounded-xl text-red-400 text-xs font-bold">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Add Achievement Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-4">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Add Achievement</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. NPFL Golden Boot 2025"
                required
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as Achievement['category'] }))}
                required
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              >
                <option value="" className="bg-gray-900">Select category...</option>
                {ACHIEVEMENT_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value} className="bg-gray-900">{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                placeholder="e.g. 2025"
                min="1900"
                max={new Date().getFullYear() + 1}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Competition</label>
              <input
                type="text"
                value={formData.competition}
                onChange={(e) => setFormData(prev => ({ ...prev, competition: e.target.value }))}
                placeholder="e.g. NPFL"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Club</label>
              <input
                type="text"
                value={formData.club}
                onChange={(e) => setFormData(prev => ({ ...prev, club: e.target.value }))}
                placeholder="e.g. FC Unity"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); resetForm() }}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-xs font-bold uppercase tracking-widest"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.title || !formData.category}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 border border-green-500 rounded-xl text-black hover:bg-green-400 transition text-xs font-bold uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {saving ? 'Adding...' : 'Add Achievement'}
            </button>
          </div>
        </form>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-green-500 animate-spin" size={24} />
        </div>
      )}

      {/* Empty State */}
      {!loading && achievements.length === 0 && (
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-8 text-center">
          <Award className="mx-auto text-gray-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white">No Achievements Yet</h3>
          <p className="text-gray-400 mt-2 text-sm">
            Add your first achievement to showcase your accomplishments.
          </p>
        </div>
      )}

      {/* Achievement Cards by Category */}
      {!loading && Object.keys(groupedAchievements).length > 0 && (
        <div className="space-y-6">
          {ACHIEVEMENT_CATEGORIES.map((cat) => {
            const categoryAchievements = groupedAchievements[cat.value]
            if (!categoryAchievements || categoryAchievements.length === 0) return null

            return (
              <div key={cat.value} className="space-y-3">
                <div className="flex items-center gap-2">
                  <cat.icon size={16} className={cat.color} />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">{cat.label}s</h3>
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest">({categoryAchievements.length})</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categoryAchievements.map((achievement) => (
                    <div key={achievement.id} className={`rounded-2xl border p-4 ${cat.bg} border-white/5 hover:border-white/10 transition`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl ${cat.bg} flex items-center justify-center`}>
                            <cat.icon size={18} className={cat.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white truncate">{achievement.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 uppercase tracking-widest">
                              {achievement.year && <span>{achievement.year}</span>}
                              {achievement.competition && <span>• {achievement.competition}</span>}
                            </div>
                          </div>
                        </div>
                        {isOwner && (
                          deleteConfirm === achievement.id ? (
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                onClick={() => handleDelete(achievement.id)}
                                className="text-[10px] text-red-400 hover:text-red-300 font-bold uppercase tracking-widest"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="text-[10px] text-gray-500 hover:text-gray-400 font-bold uppercase tracking-widest"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(achievement.id)}
                              className="p-1.5 text-gray-600 hover:text-red-400 transition ml-2"
                            >
                              <Trash2 size={12} />
                            </button>
                          )
                        )}
                      </div>
                      {achievement.club && (
                        <p className="text-[10px] text-gray-500 mt-2 uppercase tracking-widest">
                          Club: {achievement.club}
                        </p>
                      )}
                      {achievement.description && (
                        <p className="text-xs text-gray-400 mt-2">{achievement.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default PlayerAchievements
